import {ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation} from '@angular/core';
import {LinkedSFNodesService} from '../../../services/linked-sf-nodes.service';
import {BaseDialogComponent, MetadataList, OrgModelLinkNode} from '@q9elements/ui-kit/common';
import {OrgModelNodesAttachmentDialogData} from './data-access/models/org-model-nodes-attachment-dialog-data.interface';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {AbstractControl, FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {forkJoin, map, Observable, take} from 'rxjs';
import {OrgModelNodesAttachment} from '../../../../shared/models/org-model-nodes-attachment.interface';
import {
  AddMetadataFormComponent,
  DialogCloseButtonComponent,
  NodeSearchComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  TextareaFieldComponent
} from '@q9elements/ui-kit/components';
import {NgIf, NgTemplateOutlet} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {get} from 'lodash';
import {formatLinkedNode} from './utils/formatLinkedNode';

@Component({
  selector: 'app-add-org-model-node-dialog',
  templateUrl: 'add-org-model-node-dialog.component.html',
  styleUrls: ['add-org-model-node-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DialogCloseButtonComponent,
    MatDialogModule,
    AddMetadataFormComponent,
    ReactiveFormsModule,
    TextareaFieldComponent,
    NgIf,
    NgTemplateOutlet,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    TranslateModule,
    FormsModule,
    InfoMessageComponent
  ],
  encapsulation: ViewEncapsulation.None
})
export class AddOrgModelNodeDialogComponent extends BaseDialogComponent {
  private linkedSfNodesService = inject(LinkedSFNodesService);
  private data = inject<OrgModelNodesAttachmentDialogData>(MAT_DIALOG_DATA);

  public dialogRef = inject(MatDialogRef<AddOrgModelNodeDialogComponent>);

  public isPreviewMode = signal(false);
  public warningMessage = signal('');
  public isAddToStoryClicked = signal(false);

  public nodeSearchControl: AbstractControl;
  public addMetadataFormControl = new FormControl();
  public linkDescriptionFormControl = new FormControl('');
  public MAX_SELECTED_AMOUNT = 50;

  public get metadataList() {
    const metadataList = get(this.addMetadataFormControl.value, 'metadataList', []);

    return metadataList as MetadataList['refNodes'];
  }

  public get selectedMetadata() {
    const {selectedMetadata = []} = this.addMetadataFormControl.value;

    return selectedMetadata as MetadataList['refNodes'];
  }

  public get groups() {
    const {groups = []} = this.addMetadataFormControl.value;

    return groups as string[];
  }

  public goToPreviewMode() {
    const metadataToPreview = this.metadataList;
    const metadataList = get(this.addMetadataFormControl?.value, 'metadataList', []);

    this.warningMessage.set('');
    this.isAddToStoryClicked.set(true);

    if (!metadataList.length && !this.nodeSearchControl.errors) {
      this.warningMessage.set('CORE.MAP.NO_METADATA_SELECTED');
    }

    if (!metadataToPreview.length) {
      this.error = 'CORE.ATTACHMENTS.NO_METADATA_SELECTED';

      return;
    }

    this.isPreviewMode.set(true);
  }

  public onNodeSearchLoaded(nodeSearch: NodeSearchComponent) {
    this.nodeSearchControl = nodeSearch.searchControl;
  }

  public setWarningMessage(message: string) {
    this.warningMessage.set(message);
  }

  protected sendSubmitRequest() {
    this.isSubmitted = true;
    this.warningMessage.set('');

    return this.attachNodesToActivity();
  }

  private createLinkedNodes(): Observable<OrgModelLinkNode>[] {
    const nodes = this.selectedMetadata;

    return nodes.map(node => this.linkedSfNodesService.getLinkedSFNode(node.sfNode));
  }

  private createOrgModelNodesAttachments(): Observable<OrgModelNodesAttachment[]> {
    const description = this.linkDescriptionFormControl.value;
    const itemMxKey = +this.data.activityMxKey;

    const linkedNodes$ = this.createLinkedNodes();

    return forkJoin(linkedNodes$).pipe(
      map(linkedNodes =>
        linkedNodes.map<OrgModelNodesAttachment>((linkedNode: OrgModelLinkNode) => {
          return formatLinkedNode(linkedNode, this.groups, description, itemMxKey);
        })
      )
    );
  }

  private attachNodesToActivity() {
    const nodesAttachments$ = this.createOrgModelNodesAttachments();

    return nodesAttachments$.pipe(take(1));
  }
}
